import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { LevelSelect, setAlertLevel, AlertLevel } from "@/components/devices/tabs/ai-workflows/DeviceAlertSubscribers";

const db = supabase as any;

export const CameraAlertsCard = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data, error, isLoading } = useQuery({
    queryKey: ["my-camera-alerts", user?.id],
    enabled: !!user?.id,
    retry: false,
    queryFn: async () => {
      const { data: subs, error } = await db.from("device_alert_subscriptions").select("device_id, level").eq("user_id", user!.id).eq("channel", "email");
      if (error) throw error;
      const ids = (subs || []).map((s: any) => s.device_id);
      const names: Record<string, string> = {};
      if (ids.length) {
        const { data: devs } = await supabase.from("device_details").select("device_id, device_name").in("device_id", ids);
        (devs || []).forEach((d: any) => { names[d.device_id] = d.device_name || d.device_id; });
      }
      return (subs || []).map((s: any) => ({ ...s, name: names[s.device_id] || s.device_id })) as { device_id: string; level: AlertLevel; name: string }[];
    },
  });

  if (error) return null; // feature not set up yet

  const refresh = () => qc.invalidateQueries({ queryKey: ["my-camera-alerts"] });
  const change = async (deviceId: string, level: AlertLevel) => {
    try { await setAlertLevel(user!.id, deviceId, level); await refresh(); toast.success("Alert preference saved"); }
    catch (e: any) { toast.error(`Couldn't save: ${e?.message || "unknown error"}`); }
  };
  const unsubscribeAll = async () => {
    const { error } = await db.from("device_alert_subscriptions").delete().eq("user_id", user!.id);
    if (error) toast.error("Couldn't unsubscribe"); else { await refresh(); toast.success("Unsubscribed from all camera alerts"); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BellRing className="h-5 w-5 text-primary" />
          <CardTitle>Camera alerts</CardTitle>
        </div>
        <CardDescription>Cameras that email you alerts or every new image. Subscribe from a camera's AI settings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && (data?.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">You're not subscribed to any cameras.</p>}
        {data?.map((s) => (
          <div key={s.device_id} className="flex items-center justify-between gap-3">
            <span className="text-sm truncate">{s.name}</span>
            <LevelSelect value={s.level} onChange={(v) => change(s.device_id, v)} />
          </div>
        ))}
        {(data?.length ?? 0) > 0 && (
          <Button variant="outline" size="sm" onClick={unsubscribeAll}>Unsubscribe all</Button>
        )}
      </CardContent>
    </Card>
  );
};
